/**
 * reducers/constants.ts
 * desc: client constants
 * note: connects to third-party systems via Y Meadows API endpoints
 *
 */

import type { Reducer } from '@reduxjs/toolkit';
import { sortAz } from "../../utilities";
import type { ConstantsAction } from '../actions/constants';
import type { ConstantDto } from '../../types/api/constants';
import type { ComponentInfo } from '../../types/api/components';

export interface ConstantsState {
    isLoading: {
        createConstant: boolean;
        deleteConstant: boolean;
        getConstants: boolean;
        getConstant: boolean;
        getConstantUsage: boolean;
        // absent until the first update is dispatched — INITIAL_STATE never sets it
        updateConstant?: boolean;
    };
    // an empty object until a constant is created, fetched or updated
    detail: ConstantDto | Record<string, never>;
    list: ConstantDto[];
    constantUsage: ComponentInfo[];
}

const INITIAL_STATE: ConstantsState = {
    isLoading: {
        createConstant: false,
        deleteConstant: false,
        getConstants: true,
        getConstant: false,
        getConstantUsage: false,
    },
    detail: {},
    list: [],
    constantUsage: [],
};


function constantsReducer(state: ConstantsState = INITIAL_STATE, action: ConstantsAction): ConstantsState {
    switch (action.type) {
        // CLEAR Constants
        case 'CLEAR_CONSTANTS':
            return _clearConstants();

        // CREATE Constant (Instance)
        case 'CREATE_CONSTANT_ERROR':
            return _createConstantError(state);
        case 'CREATE_CONSTANT_REQUEST':
            return _createConstantRequest(state);
        case 'CREATE_CONSTANT_SUCCESS':
            return _createConstantSuccess(state, action.payload);

        // DELETE Constant (Instance)
        case 'DELETE_CONSTANT_ERROR':
            return _deleteConstantError(state);
        case 'DELETE_CONSTANT_REQUEST':
            return _deleteConstantRequest(state);
        case 'DELETE_CONSTANT_SUCCESS':
            return _deleteConstantSuccess(state, action.payload);

        // GET Constants (Collection)
        case 'GET_CONSTANTS_ERROR':
            return _getConstantsError(state);
        case 'GET_CONSTANTS_REQUEST':
            return _getConstantsRequest(state);
        case 'GET_CONSTANTS_SUCCESS':
            return _getConstantsSuccess(state, action.payload);

        // GET Constant (Instance)
        case 'GET_CONSTANT_ERROR':
            return _getConstantError(state);
        case 'GET_CONSTANT_REQUEST':
            return _getConstantRequest(state);
        case 'GET_CONSTANT_SUCCESS':
            return _getConstantSuccess(state, action.payload);

        // GET Constant Usage (Instance)
        case 'GET_CONSTANT_USAGE_ERROR':
            return _getConstantUsageError(state);
        case 'GET_CONSTANT_USAGE_REQUEST':
            return _getConstantUsageRequest(state);
        case 'GET_CONSTANT_USAGE_SUCCESS':
            return _getConstantUsageSuccess(state, action.payload);

        // Update Constant (Instance)
        case 'UPDATE_CONSTANT_ERROR':
            return _updateConstantError(state);
        case 'UPDATE_CONSTANT_REQUEST':
            return _updateConstantRequest(state);
        case 'UPDATE_CONSTANT_SUCCESS':
            return _updateConstantSuccess(state, action.payload);

        default:
            return state;
    }
}



//*************************************************
//*****   CLEAR Constants       *******************
//*************************************************
function _clearConstants(): ConstantsState {
    return INITIAL_STATE;
}



//*************************************************
//*****   CREATE Constant (Instance)   ************
//*************************************************
function _createConstantError(state: ConstantsState): ConstantsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createConstant: false,
        },
    };
}

function _createConstantRequest(state: ConstantsState): ConstantsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createConstant: true,
        },
    };
}

function _createConstantSuccess(state: ConstantsState, payload: ConstantDto): ConstantsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createConstant: false,
        },
        detail: payload,
        list: sortAz([ ...state.list, payload ], 'name'),
    };
}



//*************************************************
//*****   DELETE Constant (Instance)   ************
//*************************************************
function _deleteConstantError(state: ConstantsState): ConstantsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteConstant: false,
        },
    };
}

function _deleteConstantRequest(state: ConstantsState): ConstantsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteConstant: true,
        },
    };
}

function _deleteConstantSuccess(state: ConstantsState, constantId: string): ConstantsState {
    // create new list without deleted constant
    const [ ...constantsList ] = state.list;
    const deletedIndex = constantsList.findIndex(x => x.id === constantId);
    if (deletedIndex > -1) constantsList.splice(deletedIndex, 1);

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteConstant: false,
        },
        list: sortAz(constantsList, 'name'),
    };
}



//*************************************************
//*****   GET Constants (Collection)   ************
//*************************************************
function _getConstantsError(state: ConstantsState): ConstantsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getConstants: false,
        },
    };
}

function _getConstantsRequest(state: ConstantsState): ConstantsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getConstants: true,
        },
    };
}

function _getConstantsSuccess(state: ConstantsState, payload: ConstantDto[]): ConstantsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getConstants: false,
        },
        list: sortAz(payload, 'name'),
    };
}



//*************************************************
//*****   GET Constant (Instance)   ***************
//*************************************************
function _getConstantError(state: ConstantsState): ConstantsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getConstant: false,
        },
    };
}

function _getConstantRequest(state: ConstantsState): ConstantsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getConstant: true,
        },
    };
}

function _getConstantSuccess(state: ConstantsState, payload: ConstantDto): ConstantsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getConstant: false,
        },
        detail: payload,
    };
}



//*************************************************
//*****   GET Constant Usage (Instance)   *********
//*************************************************
function _getConstantUsageError(state: ConstantsState): ConstantsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getConstantUsage: false,
        },
    };
}

function _getConstantUsageRequest(state: ConstantsState): ConstantsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getConstantUsage: true,
        },
    };
}

function _getConstantUsageSuccess(state: ConstantsState, payload: ComponentInfo[]): ConstantsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getConstantUsage: false,
        },
        constantUsage: payload,
    };
}



//*************************************************
//*****   UPDATE Constant (Instance)     **********
//*************************************************
function _updateConstantError(state: ConstantsState): ConstantsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateConstant: false,
        },
    };
}

function _updateConstantRequest(state: ConstantsState): ConstantsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateConstant: true,
        },
    };
}

function _updateConstantSuccess(state: ConstantsState, payload: ConstantDto): ConstantsState {
    let constantsList = [ ...state.list ];

    // if list exists, modify w updated constant
    // note: list will not exist if deeplinked or refreshed detail page
    if (constantsList.length) {
        const updatedIndex = constantsList.findIndex(x => x.id === payload.id);
        if (updatedIndex > -1) constantsList[updatedIndex] = payload;
        constantsList = sortAz(constantsList, 'name');
    }

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateConstant: false,
        },
        detail: payload,
        list: constantsList,
    };
}

export default constantsReducer as Reducer<ConstantsState>;
